import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import BrandLogo from "../components/BrandLogo";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { toast } from "sonner";
import { 
  LogOut,
  Building2,
  ExternalLink,
  MessageSquare,
  FileText,
  CheckCircle2,
  Clock3,
  TrendingUp,
  LayoutGrid,
  List,
  Landmark,
  BookOpen
} from "lucide-react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis
} from "recharts";


const SOURCE_LABELS = {
  client_request: "Solicitada pelo cliente",
  manual_admin: "Direcionada pelo admin",
  auto_critical: "Destaque automático antigo"
};

const AGE_COLOR_CLASSES = {
  green: "border-l-emerald-500 bg-emerald-50/40",
  yellow: "border-l-amber-500 bg-amber-50/50",
  red: "border-l-rose-500 bg-rose-50/60",
  slate: "border-l-slate-300 bg-slate-50"
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const parseCurrencyInput = (value) => {
  const normalized = String(value || "")
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrencyInputValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return formatCurrency(value);
};

const getAgingMeta = (referral) => {
  const days = Number(referral?.status_age_days || 0);
  const colorKey = referral?.status_age_color || "green";
  const label = days > 0 ? `${days} dia${days > 1 ? "s" : ""} nesta etapa` : "Movimentado hoje";
  return {
    label,
    className: AGE_COLOR_CLASSES[colorKey] || AGE_COLOR_CLASSES.green,
    badgeClass:
      colorKey === "red"
        ? "bg-rose-100 text-rose-800"
        : colorKey === "yellow"
        ? "bg-amber-100 text-amber-800"
        : colorKey === "slate"
        ? "bg-slate-100 text-slate-700"
        : "bg-emerald-100 text-emerald-800",
  };
};

const STATUS_OPTIONS = [
  { value: "new", label: "Novo", color: "bg-blue-100 text-blue-800" },
  { value: "assigned", label: "Atribuído", color: "bg-amber-100 text-amber-800" },
  { value: "contacted", label: "Contactado", color: "bg-purple-100 text-purple-800" },
  { value: "closed", label: "Fechado", color: "bg-emerald-100 text-emerald-800" },
  { value: "not_closed_contract", label: "Não Fechou Contrato", color: "bg-rose-100 text-rose-800" }
];

const STATUS_COLORS = {
  novo: "#3b82f6",
  assigned: "#f59e0b",
  contacted: "#8b5cf6",
  not_closed_contract: "#e11d48",
  closed: "#16a34a"
};

const CONSULTANT_FAQ_ITEMS = [
  {
    value: "como-funciona",
    question: "Como funciona a plataforma para o consultor?",
    answer:
      "A plataforma conecta empresas que passaram por um diagnóstico empresarial com consultores parceiros. Você recebe encaminhamentos de empresas que solicitaram ajuda ou foram direcionadas pela administração, acompanha a etapa de cada oportunidade e registra o andamento do relacionamento dentro do próprio sistema.",
  },
  {
    value: "como-comecar",
    question: "Qual é o primeiro passo depois do cadastro?",
    answer:
      "Depois do cadastro, entre com seu e-mail e senha e acompanhe sua área de consultor. Quando houver um encaminhamento para você, a empresa aparecerá nesta tela com a área de atuação, origem do pedido e estágio atual. A partir daí, seu papel é manter o atendimento atualizado e organizado.",
  },
  {
    value: "acompanhamento",
    question: "Como devo acompanhar os clientes encaminhados?",
    answer:
      "Use esta área para atualizar status, registrar observações, mover os cards entre as etapas e acompanhar quais clientes estão novos, contactados, fechados ou não convertidos. Isso ajuda você a não perder oportunidades e permite que a equipe administrativa acompanhe o processo com clareza.",
  },
  {
    value: "observacoes",
    question: "O que devo registrar nas observações?",
    answer:
      "Registre informações objetivas e úteis: data do contato, interesse demonstrado, necessidade percebida, próximos passos combinados e eventuais impedimentos. Quanto mais claro estiver o histórico, mais fácil fica retomar o atendimento e dar continuidade ao processo comercial.",
  },
  {
    value: "valor-contrato",
    question: "Quando devo informar o valor do contrato?",
    answer:
      "Assim que houver fechamento da consultoria, registre o valor do contrato no campo específico. Esse dado alimenta o controle interno da operação e ajuda no acompanhamento financeiro da parceria. O ideal é lançar o valor assim que ele estiver confirmado.",
  },
  {
    value: "importancia",
    question: "Por que é importante manter a plataforma atualizada?",
    answer:
      "Porque a plataforma não serve apenas para receber contatos, mas para estruturar o acompanhamento das oportunidades. Quando o consultor atualiza as etapas e observações, a operação ganha organização, histórico confiável e melhores condições de conversão e acompanhamento.",
  },
  {
    value: "boas-praticas",
    question: "Quais boas práticas devo seguir?",
    answer:
      "Mantenha seu cadastro atualizado, acompanhe seus encaminhamentos com frequência, registre observações curtas e claras, mova os cards conforme a evolução real do cliente e informe corretamente o valor do contrato quando houver fechamento. Isso torna seu trabalho mais profissional e facilita a gestão da carteira.",
  },
];

const MiniTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-white/95 px-3 py-2 shadow-lg">
      {label ? <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p> : null}
      {payload.map((entry) => (
        <p key={entry.dataKey || entry.name} className="text-sm text-navy font-semibold">
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
};

const renderStatusPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value: sliceValue, total }) => {
  const pct = total > 0 ? (Number(sliceValue || 0) / total) * 100 : 0;
  if (!Number.isFinite(pct) || pct < 0.1) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 22;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#0f172a"
      fontSize={12}
      fontWeight={700}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${pct.toFixed(1)}%`}
    </text>
  );
};

const ConsultantDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [kanbanMode, setKanbanMode] = useState(() => localStorage.getItem("consultantReferralsViewMode") === "kanban");

  useEffect(() => {
    fetchReferrals();
  }, []);

  useEffect(() => {
    localStorage.setItem("consultantReferralsViewMode", kanbanMode ? "kanban" : "list");
  }, [kanbanMode]);

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API}/referrals`, { withCredentials: true });
      setReferrals(response.data.referrals || []);
    } catch (error) {
      console.error("Error fetching referrals:", error);
      toast.error("Erro ao carregar encaminhamentos");
    } finally {
      setLoading(false);
    }
  };

  const updateReferral = async (referralId, data) => {
    setUpdatingId(referralId);
    try {
      await axios.put(`${API}/referrals/${referralId}`, data, { withCredentials: true });
      toast.success("Atualizado!");
      fetchReferrals();
    } catch (error) {
      console.error("Error updating:", error);
      toast.error("Erro ao atualizar");
    } finally {
      setUpdatingId(null);
    }
  };

  const moveCardToStatus = async (referralId, targetStatus) => {
    setUpdatingId(referralId);
    try {
      await axios.put(`${API}/referrals/${referralId}`, { status: targetStatus }, { withCredentials: true });
      setReferrals((prev) => prev.map((r) => (r.referral_id === referralId ? { ...r, status: targetStatus } : r)));
      toast.success("Etapa atualizada");
    } catch (error) {
      console.error("Error moving card:", error);
      toast.error("Erro ao mover card");
    } finally {
      setUpdatingId(null);
    }
  };

  const onDragStart = (event, referralId) => {
    event.dataTransfer.setData("text/referralId", referralId);
  };

  const onDrop = (event, status) => {
    event.preventDefault();
    const referralId = event.dataTransfer.getData("text/referralId");
    if (referralId) moveCardToStatus(referralId, status);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status);
    if (!option) return <Badge className="bg-blue-100 text-blue-800">Novo</Badge>;
    return <Badge className={option.color}>{option.label}</Badge>;
  };

  const dashboardStats = useMemo(() => {
    const submitted = referrals.length;
    const closed = referrals.filter((r) => r.status === "closed").length;
    const notClosedContract = referrals.filter((r) => r.status === "not_closed_contract").length;
    const assigned = referrals.filter((r) => r.status === "assigned").length;
    const contacted = referrals.filter((r) => r.status === "contacted").length;
    const newRequests = referrals.filter((r) => !["assigned", "contacted", "closed", "not_closed_contract"].includes(r.status)).length;
    const validScores = referrals
      .map((r) => Number(r.area_score))
      .filter((score) => Number.isFinite(score) && score > 0);
    const avgScore = validScores.length
      ? Number((validScores.reduce((sum, n) => sum + n, 0) / validScores.length).toFixed(2))
      : 0;
    return {
      submitted,
      closed,
      notClosedContract,
      assigned,
      contacted,
      newRequests,
      avgScore
    };
  }, [referrals]);

  const statusChartData = useMemo(() => ([
    { name: "Novo", value: dashboardStats.newRequests, color: STATUS_COLORS.novo },
    { name: "Atribuído", value: dashboardStats.assigned, color: STATUS_COLORS.assigned },
    { name: "Contactado", value: dashboardStats.contacted, color: STATUS_COLORS.contacted },
    { name: "Não Fechou Contrato", value: dashboardStats.notClosedContract, color: STATUS_COLORS.not_closed_contract },
    { name: "Fechado", value: dashboardStats.closed, color: STATUS_COLORS.closed }
  ]), [dashboardStats]);

  const statusTotal = useMemo(
    () => statusChartData.reduce((sum, item) => sum + Number(item.value || 0), 0),
    [statusChartData]
  );

  const areaChartData = useMemo(() => {
    const grouped = {};
    referrals.forEach((referral) => {
      const area = referral.area || "Sem área";
      if (!grouped[area]) grouped[area] = { area, total: 0, fechados: 0 };
      grouped[area].total += 1;
      if (referral.status === "closed") grouped[area].fechados += 1;
    });
    return Object.values(grouped)
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [referrals]);

  const renderKanbanCard = (referral) => {
    const aging = getAgingMeta(referral);
    return (
      <div
        key={referral.referral_id}
        draggable
        onDragStart={(e) => onDragStart(e, referral.referral_id)}
        className={`border-l-4 border rounded p-3 mb-3 cursor-move hover:shadow-sm transition ${aging.className}`}
        data-testid={`consultant-kanban-card-${referral.referral_id}`}
        >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-medium text-navy text-sm truncate">{referral.company_name}</p>
            <p className="text-xs text-slate-600 mt-1">
              Área: <span className="font-medium">{referral.area}</span>
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              <Badge variant="outline">{SOURCE_LABELS[referral.source] || "Encaminhamento"}</Badge>
              {getStatusBadge(referral.status)}
              <Badge className={aging.badgeClass}>{aging.label}</Badge>
            </div>
            <p className="text-xs text-slate-500 mt-2">Criado em {formatDateTime(referral.created_at)}</p>
            <p className="text-xs text-slate-500">Entrou na etapa em {formatDateTime(referral.status_entered_at)}</p>
            {referral.has_financial_assessment && (
              <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
                <Landmark className="h-3 w-3" />
                Financeiro: {referral.financial_estado || "Indeterminado"}
              </p>
            )}
            {referral.area_score != null && (
              <p className="text-xs text-slate-500">Nota da área: {Number(referral.area_score).toFixed(2)}</p>
            )}
            {referral.contract_value > 0 && (
              <p className="text-xs text-slate-500 mt-1">
                Contrato: {formatCurrency(referral.contract_value)}
              </p>
            )}
          </div>
        </div>
        <div className="mt-3 space-y-3 rounded-md border border-slate-200 bg-white/70 p-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Status</Label>
            <Select
              value={referral.status}
              onValueChange={(value) => updateReferral(referral.referral_id, { status: value })}
              disabled={updatingId === referral.referral_id}
            >
              <SelectTrigger className="h-9 text-xs" data-testid={`kanban-status-select-${referral.referral_id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Valor do contrato</Label>
            <Input
              type="text"
              inputMode="decimal"
              defaultValue={formatCurrencyInputValue(referral.contract_value)}
              className="h-9 text-xs"
              onFocus={(e) => {
                e.target.value = referral.contract_value ?? "";
              }}
              onBlur={(e) => {
                const parsedValue = parseCurrencyInput(e.target.value);
                e.target.value = parsedValue > 0 ? formatCurrency(parsedValue) : "";
                if (String(parsedValue) !== String(referral.contract_value ?? "")) {
                  updateReferral(referral.referral_id, { contract_value: parsedValue });
                }
              }}
              data-testid={`kanban-contract-input-${referral.referral_id}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Observações</Label>
            <Textarea
              placeholder="Atualize o andamento..."
              defaultValue={referral.notes_consultant || ""}
              onBlur={(e) => {
                if (e.target.value !== referral.notes_consultant) {
                  updateReferral(referral.referral_id, { notes_consultant: e.target.value });
                }
              }}
              className="min-h-[72px] text-xs"
              data-testid={`kanban-notes-textarea-${referral.referral_id}`}
            />
          </div>
          <div className="rounded-md bg-slate-50 p-2 text-[11px] text-slate-600">
            <p>Consultor: {formatCurrency(referral.consultant_amount)}</p>
            <p>Empresa: {formatCurrency(referral.company_amount)}</p>
          </div>
        </div>
        <div className="flex items-center justify-end mt-2">
          <Link to={`/results/${referral.diagnostic_id}`}>
            <Button variant="ghost" size="sm" data-testid={`view-diagnostic-kanban-${referral.referral_id}`}>
              <ExternalLink className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/consultant" className="flex items-center gap-2">
            <BrandLogo size="md" className="max-w-[220px]" />
            <Badge variant="secondary" className="ml-2">Consultor</Badge>
          </Link>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600 hidden sm:block">{user?.name}</span>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              data-testid="consultant-logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-serif font-bold text-navy mb-2">Meus Encaminhamentos</h1>
              <p className="text-slate-600">Gerencie os clientes encaminhados para você.</p>
            </div>
            <div className="flex items-center gap-2 pt-0 sm:pt-2">
              <List className="h-4 w-4 text-slate-500" />
              <Switch checked={kanbanMode} onCheckedChange={setKanbanMode} data-testid="consultant-kanban-mode-switch" />
              <LayoutGrid className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-600">Modo Kanban</span>
            </div>
          </div>
        </div>

        <Card className="border-0 shadow-md mb-8">
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-navy" />
              Guia rápido do consultor
            </CardTitle>
            <CardDescription>
              Consulte este manual sempre que precisar entender a dinâmica da plataforma e a melhor forma de acompanhar seus clientes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {CONSULTANT_FAQ_ITEMS.map((item) => (
                <AccordionItem key={item.value} value={item.value}>
                  <AccordionTrigger className="text-left text-navy">{item.question}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">Total</span>
                <FileText className="h-4 w-4 text-slate-400" />
              </div>
              <p className="text-2xl font-bold text-navy">{dashboardStats.submitted}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">Em andamento</span>
                <Clock3 className="h-4 w-4 text-amber-500" />
              </div>
              <p className="text-2xl font-bold text-amber-600">{dashboardStats.assigned + dashboardStats.contacted}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">Fechados</span>
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
              <p className="text-2xl font-bold text-emerald-600">{dashboardStats.closed}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">Nota média da área</span>
                <TrendingUp className="h-4 w-4 text-sky-500" />
              </div>
              <p className="text-2xl font-bold text-sky-600">{dashboardStats.avgScore.toFixed(2)}</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="font-serif text-lg">Status dos Encaminhamentos</CardTitle>
              <CardDescription>Distribuição atual da sua carteira</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
                  <Pie
                    data={statusChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="46%"
                    innerRadius={52}
                    outerRadius={78}
                    paddingAngle={2}
                    label={(props) => renderStatusPieLabel({ ...props, total: statusTotal })}
                    labelLine={{ stroke: "#94a3b8", strokeWidth: 1 }}
                  >
                    {statusChartData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<MiniTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="font-serif text-lg">Atendimentos por Área</CardTitle>
              <CardDescription>Volume total e quantos já foram fechados</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={areaChartData} margin={{ left: 10, right: 15 }}>
                  <CartesianGrid strokeDasharray="4 4" vertical={false} />
                  <XAxis dataKey="area" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} interval={0} angle={-15} height={55} textAnchor="end" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<MiniTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Bar dataKey="total" fill="#0ea5e9" radius={[6, 6, 0, 0]} name="Total" />
                  <Bar dataKey="fechados" fill="#16a34a" radius={[6, 6, 0, 0]} name="Fechados" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {referrals.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Você ainda não tem encaminhamentos.</p>
            </CardContent>
          </Card>
        ) : kanbanMode ? (
          <div className="overflow-x-auto pb-3">
            <div className="flex items-start gap-4 min-w-max">
              {STATUS_OPTIONS.map((column) => {
                const columnItems = referrals.filter((r) => r.status === column.value);
                return (
                  <div
                    key={column.value}
                    className="bg-slate-50 border rounded p-3 min-h-[300px] w-[320px] shrink-0"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onDrop(e, column.value)}
                    data-testid={`consultant-kanban-column-${column.value}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-sm text-navy">{column.label}</span>
                      <Badge className={column.color}>{columnItems.length}</Badge>
                    </div>
                    {columnItems.length === 0 ? (
                      <p className="text-xs text-slate-400">Arraste cards para esta etapa</p>
                    ) : (
                      columnItems.map((referral) => renderKanbanCard(referral))
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {referrals.map((referral) => (
              <Card key={referral.referral_id} className="border-0 shadow-md">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="font-serif text-lg flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-navy" />
                        {referral.company_name}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Área: <span className="font-medium text-navy">{referral.area}</span>
                        {referral.area_score && (
                          <span className="ml-2">• Nota: {referral.area_score.toFixed(2)}/10</span>
                        )}
                      </CardDescription>
                      {referral.has_financial_assessment && (
                        <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
                          <Landmark className="h-3 w-3" />
                          Avaliação financeira disponível ({referral.financial_estado || "Indeterminado"})
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(referral.status)}
                      <Link to={`/results/${referral.diagnostic_id}`}>
                        <Button variant="ghost" size="sm" data-testid={`view-diagnostic-${referral.referral_id}`}>
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-2">
                        Atualizar Status
                      </label>
                        <Select 
                          value={referral.status}
                          onValueChange={(value) => updateReferral(referral.referral_id, { status: value })}
                          disabled={updatingId === referral.referral_id}
                        >
                        <SelectTrigger data-testid={`status-select-${referral.referral_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-2">
                        <MessageSquare className="h-4 w-4 inline mr-1" />
                        Suas Observações
                      </label>
                      <Textarea
                        placeholder="Adicione notas sobre este cliente..."
                        defaultValue={referral.notes_consultant || ""}
                        onBlur={(e) => {
                          if (e.target.value !== referral.notes_consultant) {
                            updateReferral(referral.referral_id, { notes_consultant: e.target.value });
                          }
                        }}
                        className="h-20"
                        data-testid={`notes-textarea-${referral.referral_id}`}
                      />
                    </div>
                  </div>
                  
                  {referral.notes_admin && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-sm">
                      <p className="text-xs text-amber-800 font-medium mb-1">Observações do Admin:</p>
                      <p className="text-sm text-amber-900">{referral.notes_admin}</p>
                    </div>
                  )}

                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-2">
                        Valor do contrato fechado
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        defaultValue={formatCurrencyInputValue(referral.contract_value)}
                        onFocus={(e) => {
                          e.target.value = referral.contract_value ?? "";
                        }}
                        onBlur={(e) => {
                          const parsedValue = parseCurrencyInput(e.target.value);
                          e.target.value = parsedValue > 0 ? formatCurrency(parsedValue) : "";
                          if (String(parsedValue) !== String(referral.contract_value ?? "")) {
                            updateReferral(referral.referral_id, { contract_value: parsedValue });
                          }
                        }}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid={`contract-value-input-${referral.referral_id}`}
                      />
                    </div>
                    <div className="rounded-md border bg-slate-50 p-3 text-sm text-slate-700">
                      <p className="font-medium text-navy mb-2">Previsão financeira</p>
                      <p>Consultor: {formatCurrency(referral.consultant_amount)}</p>
                      <p>Empresa: {formatCurrency(referral.company_amount)}</p>
                      <p className="text-xs text-slate-500 mt-2">
                        Regra atual: {Number(referral.consultant_split_percent ?? 70).toFixed(0)}% consultor / {Number(referral.company_split_percent ?? 30).toFixed(0)}% empresa
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ConsultantDashboard;
